import localforage from 'localforage';

// New Persistence Strategy (no compression):
// - Use IndexedDB (localforage) with raw JSON string values.
// - Debounce writes to coalesce rapid state changes and avoid UI jank.
// - Graceful fallback to in-memory map if IndexedDB errors.

localforage.config({
  name: 'interactive-map',
  version: 1.0,
  storeName: 'persist_store',
});

// Memory fallback store (only used if IndexedDB fails)
const memoryStore = new Map();

// Debounce implementation shared across keys.
// We maintain a pending map of key -> latest value and a single timer.
let pendingWrites = new Map();
let debounceTimer = null;
const DEBOUNCE_MS = 300; // tweak if needed

async function flushWrites() {
  const entries = Array.from(pendingWrites.entries());
  pendingWrites = new Map();
  debounceTimer = null;
  for (const [key, value] of entries) {
    try {
      // value expected to be a JSON string already; store directly
      await localforage.setItem(key, value);
    } catch (e) {
      memoryStore.set(key, value);
    }
  }
}

function scheduleWrite(key, value) {
  pendingWrites.set(key, value);
  if (debounceTimer) return; // timer already scheduled
  debounceTimer = setTimeout(() => {
    flushWrites();
  }, DEBOUNCE_MS);
}

export const persistStorage = {
  getItem: async (name) => {
    // Try IndexedDB first
    try {
      const stored = await localforage.getItem(name);
      if (stored != null) return stored; // stored is already raw JSON string
    } catch (e) {
      // fall through to memory
    }
    return memoryStore.get(name) || null;
  },
  setItem: async (name, value) => {
    // value is expected to be a JSON string (Zustand persist passes string)
    // Schedule debounced write; don't await to keep UI responsive.
    scheduleWrite(name, value);
  },
  removeItem: async (name) => {
    // Ensure pending write removed first
    pendingWrites.delete(name);
    try {
      await localforage.removeItem(name);
    } catch (e) {
      memoryStore.delete(name);
    }
  },
};

// export async function getStoredSizeReport() {
//   // Flush pending writes to measure accurate sizes
//   if (debounceTimer) {
//     clearTimeout(debounceTimer);
//     await flushWrites();
//   }
//   const keys = await localforage.keys();
//   const report = [];
//   for (const k of keys) {
//     if (k.includes('_chunk_') || k.endsWith('_meta')) continue; // future-proof skipping
//     try {
//       const val = await localforage.getItem(k);
//       const size = val ? new Blob([val]).size : 0; // bytes
//       report.push({ key: k, size });
//     } catch {
//       const memVal = memoryStore.get(k);
//       const size = memVal ? new Blob([memVal]).size : 0;
//       report.push({ key: k, size });
//     }
//   }
//   return report.sort((a, b) => b.size - a.size);
// }

// export async function logStorageUsage(thresholdBytes = 4.5 * 1024 * 1024) {
//   const report = await getStoredSizeReport();
//   const total = report.reduce((acc, r) => acc + r.size, 0);
//   if (total > thresholdBytes) {
//     // eslint-disable-next-line no-console
//     console.warn('[persistStorage] Approx storage usage high:', {
//       total,
//       report,
//     });
//   }
//   return { total, report };
// }

export function isStorageDegraded() {
  return memoryStore.size > 0;
}

// immediate flush helper (e.g., before page unload) (not used yet)
export async function flushPersistStorage() {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    await flushWrites();
  }
}
